import React, { useCallback, useEffect, useState } from "react";
import { ExternalLink, Power, Save } from "lucide-react";

const emptySettings = {
  enabled: false,
  headline_ro: "",
  headline_en: "",
  body_ro: "",
  body_en: "",
};

export default function AdminMaintenance() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState(emptySettings);

  const load = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const r = await fetch("/api/admin/maintenance.php", { credentials: "include", cache: "no-store" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok || !j.settings) {
        setMsg(j.detail || "Nu s-a putut încărca setarea.");
        return;
      }
      setDraft(j.settings);
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    setMsg("");
    try {
      const r = await fetch("/api/admin/maintenance.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(draft),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setMsg(j.detail || "Salvare eșuată.");
        return;
      }
      setDraft(j.settings);
      setMsg("Salvat. Vizitatorii văd pagina de mentenanță doar dacă comutatorul este ON.");
    } catch {
      setMsg("Eroare de rețea.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = () => setDraft((d) => ({ ...d, enabled: !d.enabled }));

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl py-16 text-center text-sm text-slate-500">Se încarcă…</div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-8">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Site public</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Mod mentenanță</h1>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
          Când este activ, vizitatorii văd o pagină elegantă în locul site-ului. Panoul{" "}
          <strong className="font-semibold text-slate-800">/admin</strong> rămâne accesibil ca să poți opri
          mentenanța oricând.
        </p>
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-slate-100 bg-slate-50/80 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${
                draft.enabled ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
              }`}
            >
              <Power className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {draft.enabled ? "Mentenanță ACTIVĂ" : "Mentenanță oprită"}
              </p>
              <p className="text-xs text-slate-500">
                {draft.enabled ? "Publicul vede doar ecranul de mentenanță." : "Site-ul este vizibil normal."}
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={draft.enabled}
            onClick={toggle}
            className={`relative h-10 w-[4.5rem] shrink-0 rounded-full transition-colors ${
              draft.enabled ? "bg-rose-500" : "bg-slate-200"
            }`}
          >
            <span
              className={`absolute top-1 h-8 w-8 rounded-full bg-white shadow transition-transform ${
                draft.enabled ? "left-[calc(100%-2.25rem)]" : "left-1"
              }`}
            />
            <span className="sr-only">Comută mod mentenanță</span>
          </button>
        </div>

        <div className="space-y-5 px-6 py-6">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Mesaje pe pagina publică</p>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Titlu (RO)</span>
            <input
              value={draft.headline_ro}
              onChange={(e) => setDraft((d) => ({ ...d, headline_ro: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none ring-slate-900/5 focus:ring-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Titlu (EN)</span>
            <input
              value={draft.headline_en}
              onChange={(e) => setDraft((d) => ({ ...d, headline_en: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Text (RO)</span>
            <textarea
              rows={3}
              value={draft.body_ro}
              onChange={(e) => setDraft((d) => ({ ...d, body_ro: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Text (EN)</span>
            <textarea
              rows={3}
              value={draft.body_en}
              onChange={(e) => setDraft((d) => ({ ...d, body_en: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 underline decoration-slate-300 underline-offset-4 hover:text-slate-900"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Preview site (filă nouă)
          </a>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Se salvează…" : "Salvează setările"}
          </button>
        </div>
      </section>

      {msg ? (
        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{msg}</p>
      ) : null}

      <p className="text-xs leading-relaxed text-slate-500">
        Setările sunt salvate în <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">api/data/maintenance.json</code>{" "}
        pe server. Asigură-te că folderul <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">api/data</code> este
        inscriptibil de PHP.
      </p>
    </div>
  );
}
